import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import KanbanTaskCard from './kanbanTaskCard';
import KanbanTaskCardModal from './kanbanTaskCardModal';
import KanbanTaskAnnotationModal from './kanbanTaskAnnotationModal';
import KanbanTaskRecurringModal from './kanbanTaskRecurringModal';
import { columns } from './kanbanTaskConstants';
import useTaskBoard from './useTaskBoard';

function KanbanTaskWeb() {
  const {
    tasksByColumn,
    isLoading,
    isToday,
    formattedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    taskModalOpen,
    editingTask,
    openNewTaskModal,
    openEditTaskModal,
    closeTaskModal,
    handleSaveTask,
    recurringTasks,
    recurringModalOpen,
    setRecurringModalOpen,
    handleRecurringAdd,
    handleRecurringUpdate,
    handleRecurringDelete,
    handleRecurringMove,
    handleAdvanceStage,
    handlePreviousStage,
    deleteModal,
    handleDeleteClick,
    closeDeleteModal,
    handleConfirmDelete,
    notesModalOpen,
    notesTask,
    openNotesModal,
    closeNotesModal,
    handleAddNote,
    showSuccessMessage,
    successMessage,
    closeSuccessMessage,
    showErrorMessage,
    errorMessage,
    closeErrorMessage,
  } = useTaskBoard();

  return (
    <Box sx={{ padding: 3 }}>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={closeSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={closeErrorMessage} />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 'bold', color: 'text.secondary' }}>
            Tarefas
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={goToPreviousDay} aria-label="Dia anterior">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 190, textAlign: 'center', textTransform: 'capitalize' }}
            >
              {formattedDate}
            </Typography>
            <IconButton size="small" onClick={goToNextDay} aria-label="Próximo dia">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            {!isToday && (
              <Button size="small" onClick={goToToday} sx={{ ml: 0.5 }}>
                Hoje
              </Button>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<RepeatIcon />}
            onClick={() => setRecurringModalOpen(true)}
          >
            Tarefas recorrentes
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTaskModal}>
            Nova tarefa
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          overflowX: 'auto',
          pb: 2,
        }}
      >
        {columns.map((column, columnIndex) => {
          const columnTasks = tasksByColumn[columnIndex];

          return (
            <Paper
              key={column.id}
              elevation={0}
              sx={{
                flex: '0 0 280px',
                minWidth: 280,
                borderRadius: 2,
                borderTop: `4px solid ${column.color}`,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderTopColor: column.color,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 200px)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  {column.title}
                </Typography>
                <Chip
                  label={columnTasks.length}
                  size="small"
                  sx={{
                    backgroundColor: column.color,
                    color: '#fff',
                    fontWeight: 'bold',
                    height: 22,
                  }}
                />
              </Box>

              <Stack spacing={1.5} sx={{ px: 1.5, pb: 1.5, overflowY: 'auto', flexGrow: 1 }}>
                {columnTasks.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      color: 'text.disabled',
                      fontSize: '0.85rem',
                      py: 3,
                    }}
                  >
                    Nenhuma tarefa
                  </Box>
                ) : (
                  columnTasks.map((task) => (
                    <KanbanTaskCard
                      key={task.id}
                      task={task}
                      isFirstColumn={columnIndex === 0}
                      isLastColumn={columnIndex === columns.length - 1}
                      onEdit={openEditTaskModal}
                      onDelete={handleDeleteClick}
                      onOpenNotes={openNotesModal}
                      onAdvance={handleAdvanceStage}
                      onPrevious={handlePreviousStage}
                    />
                  ))
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <KanbanTaskCardModal
        open={taskModalOpen}
        task={editingTask}
        onClose={closeTaskModal}
        onSave={handleSaveTask}
      />

      <KanbanTaskRecurringModal
        open={recurringModalOpen}
        onClose={() => setRecurringModalOpen(false)}
        recurringTasks={recurringTasks}
        onAdd={handleRecurringAdd}
        onUpdate={handleRecurringUpdate}
        onDelete={handleRecurringDelete}
        onMove={handleRecurringMove}
      />

      <KanbanTaskAnnotationModal
        open={notesModalOpen}
        task={notesTask}
        onClose={closeNotesModal}
        onAddNote={handleAddNote}
      />

      <Dialog open={deleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir tarefa</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Esta ação remove a tarefa e suas anotações permanentemente. Deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default KanbanTaskWeb;
